import { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Icon } from '@components/icon';
import { Text } from '@components/text';
import './FooterSiteIndex.scss';

export interface FooterSiteIndexProps {
  className?: string;
  children?: React.ReactNode;
  label?: string;
}

/**
 * FooterSiteIndex component, used on mobile footer
 * @param label - dropdown text
 * @param children - element to show when dropdown is open
 */
export const FooterSiteIndex: React.FC<FooterSiteIndexProps> = ({
  label = 'Site Index',
  className,
  children,
}) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const iconName = isOpen ? 'chevron-upward' : 'chevron-downward';

  const handleOpen = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // flag to check if the dropdown was opened at least once
    if (!hasBeenOpened) setHasBeenOpened(true);

    // toggle dropdown
    setIsOpen(isOpen => !isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Space' || e.code === 'Enter') {
      handleOpen(e);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handleOpen(e);
  };

  useEffect(() => {
    // if the dropdown was opened at least once, then can scroll to the top or bottom of the dropdown
    if (hasBeenOpened) {
      // scroll to the top or bottom of the dropdown when it opens or closes
      if (isOpen) {
        labelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        labelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [isOpen, hasBeenOpened]);

  return (
    <div className={classNames('vds-footer-site-index', className)}>
      <div
        className="vds-footer-site-index__trigger"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={labelRef}
        role="button"
        aria-expanded={isOpen}
        aria-controls="vds-footer-site-index__children">
        <Text
          className="vds-footer-site-index__label"
          size="small"
          weight="regular"
          color="tertiary"
          variant="caption">
          {label}
        </Text>
        <Icon name={iconName} size="small" />
      </div>
      {isOpen && (
        <div
          ref={childrenRef}
          id="vds-footer-site-index__children"
          className="vds-footer-site-index__children">
          {children}
        </div>
      )}
    </div>
  );
};
